import { HTMLAttributes, RefObject, useRef, useState } from 'react';

import { Button, ListItem, Menu, SelectOption, Typography } from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { Icon, responsive } from '@/utils';

interface CompactMultiSelectProps<T> extends Omit<HTMLAttributes<HTMLElement>, 'onChange'> {
  onChange: (ids: SelectOption<T>) => void;
  value: SelectOption<T> | undefined;
  options: SelectOption<T>[];
  /** Render as usual but non-interactive (e.g. the current selection during HLS rewind). */
  disabled?: boolean;
}

export const CompactMenuSelect = <T,>({
  onChange,
  value,
  options,
  disabled = false,
  ...rest
}: CompactMultiSelectProps<T>) => {
  const { t } = useTranslation(['common']);
  const menuRef = useRef<HTMLElement>(null);

  const [open, setOpen] = useState(false);

  return (
    <Menu
      anchorEl={menuRef}
      open={open}
      onClose={() => setOpen(false)}
      style={{ width: 'fit-content' }}
      menuElement={options?.map((item: SelectOption<T>) => (
        <ListItem
          // Padding right to align with icon; icon width + button right padding
          // Padding left to align with button text
          style={{ padding: `0 2rem 0 ${responsive.spacing['2xlarge']}` }}
          key={`${item.id ?? item.label}`}
          onClick={() => {
            onChange(item);
            setOpen(false);
          }}
          elem={
            <Typography type='body_medium' weight={700}>
              {item.label}
            </Typography>
          }
        />
      ))}
    >
      <StyledButton
        {...rest}
        label={value?.label ?? t('noSelection')}
        ref={menuRef as RefObject<HTMLButtonElement>}
        variant='outline'
        color='action'
        size='small'
        disabled={disabled}
        tailIcon={<Icon.ChevronDown />}
        onClick={() => {
          if (!disabled) setOpen(prev => !prev);
        }}
        onMouseDown={e => e.stopPropagation()}
      />
    </Menu>
  );
};

const StyledButton = styled(Button)`
  svg {
    color: inherit;
  }
`;
